import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Products, BaselineCountryCodes } from '@/comm'
import { TotalsApp } from '@/comm/app'
import { postFetcher } from '@/hooks'
import { useRole } from '@/hooks/auth'
import useSWR, { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { create } from 'zustand'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { countryColumn, pidColumn } from './columns'
import { Role } from '@/constants/role'
import {
  ROAS_STANDARD_DAY_OPTIONS,
  getBaselineDayKey,
} from '@/features/roas/constants/roas-days'

const EMPTY_OBJECT = {}
const EMPTY_VSS = {}
const DEFAULT_MEDIA_SOURCES = [{ value: 'total', label: 'Total' }]

/** Group baselines are for aggregate ROI filters only; they do not override concrete pids. */
// 产品与国家常量由 ProductTreeLoader 加载后才有值，需在渲染期读取
const drGroupProducts = () =>
  TotalsApp.filter((p) => p.code !== 'total').map((p) => ({
    ...p,
    isGroup: true,
  }))

const drAppProducts = () => Products.filter((p) => p.group != undefined)
const drBaselineCountries = () => ['all', ...BaselineCountryCodes]

const Index = () => {
  const { data: baselinesAll } = useSWR(['/drs/baselines/all'])
  const data1 = baselinesAll ?? EMPTY_OBJECT
  const { data: singleAppsList = [] } = useSWR(['/drs/single-apps'])
  const globalTargetDay = useDrStore((state) => state.globalTargetDay)
  const globalStartDate = useDrStore((state) => state.globalStartDate)
  const globalMediaSource = useDrStore((state) => state.globalMediaSource)
  const setGlobalStartDate = useDrStore((state) => state.setGlobalStartDate)
  const { data: mediaSources = DEFAULT_MEDIA_SOURCES } = useSWR([
    '/drs/baselines/media-sources',
  ])

  const { data: vssData } = useSWR(['/drs/vs', { day_n: globalTargetDay }], {
    keepPreviousData: true,
  })
  const vss = vssData ?? EMPTY_VSS

  const enable = useDrStore((state) => state.enable)
  const showEditor = useDrStore((state) => state.showEditor)
  const showSingleAppsEditor = useDrStore((state) => state.showSingleAppsEditor)

  // 提取所有可用日期
  const availableDates = useMemo(() => {
    return Object.keys(data1).sort()
  }, [data1])

  // 初始化默认日期(选择第一个可用日期)
  useEffect(() => {
    if (availableDates.length > 0 && !globalStartDate) {
      setGlobalStartDate(availableDates[availableDates.length - 1])
    }
  }, [availableDates, globalStartDate, setGlobalStartDate])

  // 根据选择的day和日期过滤数据，并解析 single apps
  const data = useMemo(() => {
    const dayKey = getBaselineDayKey(globalTargetDay)
    const selectedData = data1[globalStartDate]?.[dayKey] || []

    // 将数组转换为按country分组的对象
    const baselinesByCountry = selectedData.reduce((acc, item) => {
      if ((item.mediaSource || 'total') !== globalMediaSource) {
        return acc
      }
      if (!acc[item.country]) {
        acc[item.country] = {}
      }
      
      // 如果有 packageName，放到 singleApps 子对象中
      if (item.packageName) {
        if (!acc[item.country].singleApps) {
          acc[item.country].singleApps = {}
        }
        if (!acc[item.country].singleApps[item.pid]) {
          acc[item.country].singleApps[item.pid] = {}
        }
        acc[item.country].singleApps[item.pid][item.packageName] = item.value
      } else {
        // 基础 baseline
        acc[item.country][item.pid] = item.value
      }
      
      return acc
    }, {})

    // 构建表格数据
    return drBaselineCountries().map((country) => ({
      country,
      baselines: baselinesByCountry[country] || {},
      vs: vss?.[country],
    }))
  }, [data1, vss, globalTargetDay, globalStartDate, globalMediaSource])

  const columns = useMemo(() => {
    const rows = [countryColumn]
    rows.push(...drGroupProducts().map((p) => pidColumn(p)))
    rows.push(...drAppProducts().map((p) => pidColumn(p)))
    return rows
  }, [])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableMultiRowSelection: true,
  })

  const onCellClick = (cell) => {
    const cellValue = cell.getValue()
    const original = cell.row.original

    // pid=14 时，弹出 single apps 编辑器（支持查看和编辑）
    if (cellValue.pid === '14') {
      showSingleAppsEditor({
        pid: cellValue.pid,
        country: original.country,
        baseDr: cellValue.dr,
        singleApps: cellValue.singleApps || {},
        enableEdit: enable, // 传递编辑模式状态
      })
    } else if (enable) {
      // 其他 pid 只有在编辑模式下才能点击
      showEditor({
        pid: cellValue.pid,
        country: original.country,
        before: cellValue.dr,
      })
    }
  }

  return (
    <>
      <EditorDialog />
      <SingleAppsDialog singleAppsList={singleAppsList} />
      <BatchCreateDialog data={data1} />
      <EditButton availableDates={availableDates} mediaSources={mediaSources} />
      <div className="text-xs text-muted-foreground mb-2">
        Group columns (Sticker / Neku / …) apply only when ROI filters use that
        aggregate pid. Concrete product columns never inherit group baselines.
      </div>
      <DataTableWithBorder table={table} onCellClick={onCellClick} />
    </>
  )
}

const EditButton = ({ availableDates = [], mediaSources = DEFAULT_MEDIA_SOURCES }) => {
  const isBuyerLeader = useRole(Role.BuyerLeader)
  const {
    enable,
    toggleEnable,
    globalTargetDay,
    setGlobalTargetDay,
    globalStartDate,
    setGlobalStartDate,
    globalMediaSource,
    setGlobalMediaSource,
  } = useDrStore()
  if (!isBuyerLeader) return null
  return (
    <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap">
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium">Target Day:</label>
        <Select
          value={String(globalTargetDay)}
          onValueChange={(val) => setGlobalTargetDay(Number(val))}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROAS_STANDARD_DAY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium">Start Date:</label>
        <Select value={globalStartDate} onValueChange={setGlobalStartDate}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Select Date" />
          </SelectTrigger>
          <SelectContent>
            {availableDates.map((date) => (
              <SelectItem key={date} value={date}>
                {date}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium">Channel:</label>
        <Select value={globalMediaSource} onValueChange={setGlobalMediaSource}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Select Channel" />
          </SelectTrigger>
          <SelectContent>
            {mediaSources.map((source) => (
              <SelectItem key={source.value} value={source.value}>
                {source.label || source.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="enable-editor"
          checked={enable}
          onCheckedChange={toggleEnable}
        />
        <label
          htmlFor="terms"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Enable editor model
        </label>
      </div>
      <div className="flex items-center space-x-2">
        <Button
          onClick={() => useDrStore.getState().showBatchCreate()}
          variant="outline"
        >
          Batch Create
        </Button>
      </div>
    </div>
  )
}

export const useDrStore = create((set) => ({
  enable: false,
  showBatchDialog: false,
  batchTargetDay: 0,
  batchStartDate: '',
  batchMode: 'empty',
  showBatchCreate: () => set({ showBatchDialog: true }),
  hideBatchCreate: () => set({ showBatchDialog: false }),
  setBatchTargetDay: (day) => set({ batchTargetDay: day }),
  setBatchStartDate: (date) => set({ batchStartDate: date }),
  batchSourceDate: '',
  setBatchSourceDate: (date) => set({ batchSourceDate: date }),
  setBatchMode: (mode) => set({ batchMode: mode }),
  // 普通编辑器状态
  show: false,
  pid: '',
  country: '',
  before: 0,
  // Single Apps 编辑器状态
  showSingleApps: false,
  singleAppsPid: '',
  singleAppsCountry: '',
  singleAppsBaseDr: 0,
  singleAppsData: {},
  singleAppsEnableEdit: false,
  // 全局选择器的值
  globalTargetDay: 0,
  globalStartDate: '',
  globalMediaSource: 'total',
  // 当前编辑记录的值
  editTargetDay: 0,
  editStartDate: '2025-01-01',
  editMediaSource: 'total',
  toggleEnable: () => set((state) => ({ enable: !state.enable })),
  setGlobalTargetDay: (globalTargetDay) => set({ globalTargetDay }),
  setGlobalStartDate: (globalStartDate) => set({ globalStartDate }),
  setGlobalMediaSource: (globalMediaSource) => set({ globalMediaSource }),
  showEditor: ({ pid, country, before }) =>
    set((state) => ({
      pid,
      country,
      before,
      editTargetDay: state.globalTargetDay,
      editStartDate: state.globalStartDate,
      editMediaSource: state.globalMediaSource,
      show: true,
    })),
  hideEditor: () => set({ show: false }),
  showSingleAppsEditor: ({ pid, country, baseDr, singleApps, enableEdit }) =>
    set((state) => ({
      singleAppsPid: pid,
      singleAppsCountry: country,
      singleAppsBaseDr: baseDr,
      singleAppsData: singleApps,
      singleAppsEnableEdit: enableEdit,
      editTargetDay: state.globalTargetDay,
      editStartDate: state.globalStartDate,
      editMediaSource: state.globalMediaSource,
      showSingleApps: true,
    })),
  hideSingleAppsEditor: () => set({ showSingleApps: false }),
}))

const FormSchema = z.object({
  dr: z
    .union([z.string(), z.number()])
    .refine((val) => val !== '' && val !== null && val !== undefined, {
      message: 'dr cannot be empty',
    })
    .refine((val) => !isNaN(Number(val)), { message: 'dr must be a number' })
    .refine((val) => Number(val) >= 0, { message: 'dr must be >= 0' }),
  targetDay: z.number(),
  startDate: z.string(),
})

const EditorDialog = () => {
  const { pid, country, before, editTargetDay, editStartDate, editMediaSource } = useDrStore()

  const show = useDrStore((state) => state.show)
  const { hideEditor } = useDrStore()

  const { trigger } = useSWRMutation('/drs/baselines', postFetcher)
  const onSubmit = (data) => {
    trigger(
      {
        pid,
        country,
        value: Number(data.dr),
        targetDay: data.targetDay,
        startDate: data.startDate,
        mediaSource: editMediaSource,
      },
      {
        onSuccess: () => {
          hideEditor()
          mutate(['/drs/baselines/all'])
        },
      },
    )
  }

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      dr: before !== undefined ? before : 0,
      targetDay: editTargetDay,
      startDate: editStartDate,
    },
  })

  const {
    formState: { isValid, isSubmitting },
    setValue,
  } = form

  useEffect(() => {
    setValue('dr', before !== undefined ? before : 0)
    setValue('targetDay', editTargetDay)
    setValue('startDate', editStartDate)
  }, [before, editTargetDay, editStartDate, setValue])

  return (
    <Dialog open={show} onOpenChange={hideEditor}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit DR Baseline</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pid:</span>
              <span className="font-medium">{pid}</span>
            </div>
            {String(pid).startsWith('total') && (
              <div className="text-xs text-muted-foreground rounded border border-dashed p-2">
                Group baseline: used only for aggregate ROI filters with this
                group pid. It does not override concrete product baselines.
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Country:</span>
              <span className="font-medium">{country}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Value:</span>
              <span className="font-medium">{before}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Target Day:</span>
              <span className="font-medium">Day{editTargetDay}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Start Date:</span>
              <span className="font-medium">{editStartDate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Channel:</span>
              <span className="font-medium">{editMediaSource}</span>
            </div>
          </div>
          <div className="grid gap-4 py-4">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="dr"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="Enter DR value..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogClose asChild>
                  <Button disabled={!isValid || isSubmitting} type="submit" className="w-full">
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </DialogClose>
              </form>
            </Form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Single Apps 编辑器对话框
const SingleAppsDialog = ({ singleAppsList = [] }) => {
  const { 
    showSingleApps, 
    singleAppsPid, 
    singleAppsCountry, 
    singleAppsBaseDr, 
    singleAppsData,
    singleAppsEnableEdit,
    editTargetDay, 
    editStartDate,
    editMediaSource,
    hideSingleAppsEditor 
  } = useDrStore()

  const { trigger, isMutating } = useSWRMutation('/drs/baselines', postFetcher)

  const handleSubmit = (packageName, value) => {
    if (!singleAppsEnableEdit || isMutating || value.trim() === '') return

    const nextValue = Number(value)
    const currentValue = packageName
      ? singleAppsData?.[packageName]
      : singleAppsBaseDr
    if (
      !Number.isFinite(nextValue) ||
      nextValue < 0 ||
      (currentValue != null && nextValue === Number(currentValue))
    ) {
      return
    }

    trigger(
      {
        pid: singleAppsPid,
        packageName: packageName || null,
        country: singleAppsCountry,
        value: nextValue,
        targetDay: editTargetDay,
        startDate: editStartDate,
        mediaSource: editMediaSource,
      },
      {
        onSuccess: () => {
          // 保存成功后更新比较基准，避免同一个值再次失焦时重复提交。
          useDrStore.setState((state) => {
            if (
              state.singleAppsPid !== singleAppsPid ||
              state.singleAppsCountry !== singleAppsCountry ||
              state.editTargetDay !== editTargetDay ||
              state.editStartDate !== editStartDate ||
              state.editMediaSource !== editMediaSource
            ) {
              return state
            }
            return packageName
              ? {
                  singleAppsData: {
                    ...state.singleAppsData,
                    [packageName]: nextValue,
                  },
                }
              : { singleAppsBaseDr: nextValue }
          })
          mutate(['/drs/baselines/all'])
        },
      },
    )
  }

  return (
    <Dialog open={showSingleApps} onOpenChange={hideSingleAppsEditor}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{singleAppsEnableEdit ? 'Edit' : 'View'} Single Apps Baseline</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 overflow-hidden">
          <div className="grid grid-cols-4 gap-4 text-sm bg-muted/50 p-3 rounded-lg">
            <div>
              <span className="text-muted-foreground">Country:</span>
              <span className="ml-2 font-medium">{singleAppsCountry}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Target Day:</span>
              <span className="ml-2 font-medium">Day{editTargetDay}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Start Date:</span>
              <span className="ml-2 font-medium">{editStartDate}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Channel:</span>
              <span className="ml-2 font-medium">{editMediaSource}</span>
            </div>
          </div>

          {/* 基础 pid=14 值 */}
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="font-medium">Base (pid=14)</div>
                <div className="text-xs text-muted-foreground">Default baseline for all single apps</div>
              </div>
              {singleAppsEnableEdit ? (
                <Input 
                  type="number" 
                  step="0.01" 
                  defaultValue={singleAppsBaseDr ?? ''}
                  disabled={isMutating}
                  className="w-28 text-right"
                  onBlur={(e) => handleSubmit(null, e.target.value)}
                />
              ) : (
                <span className="font-mono text-lg">{singleAppsBaseDr ?? '-'}</span>
              )}
            </div>
          </div>

          <div className="text-sm font-medium text-muted-foreground">Individual Apps:</div>

          {/* Single Apps 列表 */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0 max-h-[400px]">
            {singleAppsList.map((app) => {
              const currentValue = singleAppsData?.[app.name]
              return (
                <div key={app.name} className="flex items-center justify-between gap-4 p-3 border rounded-lg hover:bg-muted/30">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium capitalize">{app.displayName || app.name}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{app.pn}</div>
                  </div>
                  {singleAppsEnableEdit ? (
                    <Input 
                      type="number" 
                      step="0.01" 
                      placeholder={String(singleAppsBaseDr ?? '')}
                      defaultValue={currentValue ?? ''}
                      disabled={isMutating}
                      className="w-28 text-right shrink-0"
                      onBlur={(e) => handleSubmit(app.name, e.target.value)}
                    />
                  ) : (
                    <span className="font-mono text-lg shrink-0">{currentValue ?? (singleAppsBaseDr ?? '-')}</span>
                  )}
                </div>
              )
            })}
          </div>

          <DialogClose asChild>
            <Button variant="outline" className="w-full">
              Close
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default Index

const BatchCreateDialog = ({ data = {} }) => {
  const { showBatchDialog, batchTargetDay, batchStartDate, batchMode, batchSourceDate, globalMediaSource, hideBatchCreate, setBatchTargetDay, setBatchStartDate, setBatchMode, setBatchSourceDate } = useDrStore()
  const { trigger, isMutating } = useSWRMutation('/drs/baselines/batch', postFetcher)

  const handleSubmit = () => {
    if (!batchStartDate) {
      alert('Please select start date')
      return
    }
    if (batchMode === 'copy' && !batchSourceDate) {
      alert('Please select source date')
      return
    }
    trigger(
      { targetDay: batchTargetDay, startDate: batchStartDate, mode: batchMode, sourceDate: batchSourceDate, mediaSource: globalMediaSource },
      {
        onSuccess: () => {
          hideBatchCreate()
          mutate(['/drs/baselines/all'])
          alert('Batch create success!')
        },
        onError: (error) => {
          alert('Create failed: ' + (error.message || 'Unknown error'))
        }
      }
    )
  }

  // Filter available dates based on target day
  const availableDates = useMemo(() => {
    const dayKey = getBaselineDayKey(batchTargetDay)
    return Object.keys(data).filter(date => {
      const dayData = data[date]?.[dayKey]
      return (
        dayData &&
        dayData.some((item) => (item.mediaSource || 'total') === globalMediaSource)
      )
    }).sort()
  }, [data, batchTargetDay, globalMediaSource])

  // Effect to set default source date if available
  useEffect(() => {
    if (availableDates.length > 0) {
      if (!batchSourceDate || !availableDates.includes(batchSourceDate)) {
        setBatchSourceDate(availableDates[availableDates.length - 1])
      }
    } else {
      setBatchSourceDate('')
    }
  }, [availableDates, batchSourceDate, setBatchSourceDate])

  return (
    <Dialog open={showBatchDialog} onOpenChange={hideBatchCreate}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Batch Create Baseline</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>Channel</Label>
            <Input value={globalMediaSource} readOnly />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Target Day</Label>
            <Select value={String(batchTargetDay)} onValueChange={(val) => setBatchTargetDay(Number(val))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROAS_STANDARD_DAY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Start Date</Label>
            <Input type="date" value={batchStartDate} onChange={(e) => setBatchStartDate(e.target.value)} />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Mode</Label>
            <div className="flex flex-col gap-2">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="empty"
                  name="batchMode"
                  value="empty"
                  checked={batchMode === 'empty'}
                  onChange={(e) => setBatchMode(e.target.value)}
                  className="h-4 w-4"
                />
                <Label htmlFor="empty" className="font-normal cursor-pointer">Empty batch</Label>
              </div>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="copy"
                    name="batchMode"
                    value="copy"
                    checked={batchMode === 'copy'}
                    onChange={(e) => setBatchMode(e.target.value)}
                    disabled={availableDates.length === 0}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="copy" className={`font-normal cursor-pointer ${availableDates.length === 0 ? 'text-gray-400' : ''}`}>
                    Copy from date {availableDates.length === 0 && '(no data)'}
                  </Label>
                </div>
                {batchMode === 'copy' && (
                  <div className="pl-6">
                    <Select value={batchSourceDate} onValueChange={setBatchSourceDate}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select source date" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDates.map(date => (
                          <SelectItem key={date} value={date}>{date}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={isMutating || !batchStartDate}>
            {isMutating ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
